import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import adminSecurityApi from '../services/adminSecurityApi';
import { formatApiError } from '../utils/errorMessage';
import ErrorBoundary from '../components/ErrorBoundary';

const AdminSecurity = () => {
  const { user } = useAuth();
  const [blockedRequests, setBlockedRequests] = useState([]);
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ ip_address: '', reason: '' });
  const [adding, setAdding] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const canView = user && (user.is_superuser || user.role === 'superuser');

  useEffect(() => {
    if (canView) {
      loadBlockedRequests();
      loadExemptions();
    }
  }, [canView]);

  const loadBlockedRequests = async () => {
    try {
      setLoading(true);
      const res = await adminSecurityApi.listBlockedRequests({ limit: 50 });
      setBlockedRequests(res.data);
      setError('');
    } catch (e) {
      setError(formatApiError(e, 'Failed to load blocked requests'));
    } finally {
      setLoading(false);
    }
  };

  const loadExemptions = async () => {
    try {
      const res = await adminSecurityApi.listExemptions();
      setExemptions(res.data || []);
    } catch (e) {
      setError(formatApiError(e, 'Failed to load exemptions'));
    }
  };

  const handleExemptFromRow = (ipAddress) => {
    setForm({ ip_address: ipAddress, reason: '' });
  };

  const handleAddExemption = async (e) => {
    e.preventDefault();
    if (!form.ip_address) {
      setError('An IP address is required');
      return;
    }
    try {
      setAdding(true);
      await adminSecurityApi.addExemption(form);
      setForm({ ip_address: '', reason: '' });
      setError('');
      await loadExemptions();
    } catch (e) {
      setError(formatApiError(e, 'Failed to add exemption'));
    } finally {
      setAdding(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      setRevoking(true);
      await adminSecurityApi.revokeExemption(id);
      setRevokingId(null);
      setError('');
      await loadExemptions();
    } catch (e) {
      setError(formatApiError(e, 'Failed to revoke exemption'));
    } finally {
      setRevoking(false);
    }
  };

  if (!canView) {
    return <div className="p-6">You do not have permission to view this page.</div>;
  }

  const activeExemptions = exemptions.filter((ex) => ex.is_active);
  const revokedExemptions = exemptions.filter((ex) => !ex.is_active);

  return (
    <ErrorBoundary section="Admin Security">
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Security</h2>
          <p className="text-gray-600">Review anti-automation blocks and manage IP exemptions</p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">
            {formatApiError(error, 'An error occurred')}
          </div>
        )}

        <div className="bg-white rounded shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Exempt an IP</h3>
          <form onSubmit={handleAddExemption} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              required
              placeholder="IP address"
              value={form.ip_address}
              onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
              className="border rounded px-3 py-2"
            />
            <input
              type="text"
              placeholder="Reason (optional)"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="border rounded px-3 py-2 md:col-span-2"
            />
            <div className="md:col-span-3 flex justify-end">
              <button type="submit" disabled={adding} className="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-blue-300">
                {adding ? 'Adding...' : 'Add Exemption'}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded shadow">
          <div className="p-6 border-b"><h3 className="text-lg font-semibold">Active Exemptions</h3></div>
          <div className="divide-y">
            {activeExemptions.length === 0 ? (
              <div className="p-6 text-gray-500">No active exemptions</div>
            ) : (
              activeExemptions.map((ex) => (
                <div key={ex.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{ex.ip_address}</div>
                      <div className="text-sm text-gray-500">
                        {ex.reason || 'No reason given'} — exempted by {ex.exempted_by_email || 'unknown'} on {new Date(ex.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => setRevokingId(ex.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >
                      Revoke
                    </button>
                  </div>

                  {revokingId === ex.id && (
                    <div className="mt-4 border-t pt-4">
                      <div className="text-sm font-semibold mb-2">Revoke exemption for {ex.ip_address}?</div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRevoke(ex.id)}
                          disabled={revoking}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300"
                        >
                          {revoking ? 'Revoking...' : 'Confirm Revoke'}
                        </button>
                        <button
                          onClick={() => setRevokingId(null)}
                          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {revokedExemptions.length > 0 && (
          <div className="bg-white rounded shadow">
            <div className="p-6 border-b"><h3 className="text-lg font-semibold">Revoked Exemptions</h3></div>
            <div className="divide-y">
              {revokedExemptions.map((ex) => (
                <div key={ex.id} className="p-4 text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{ex.ip_address}</span> — revoked by {ex.revoked_by_email || 'unknown'} on {new Date(ex.revoked_at).toLocaleString()}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded shadow">
          <div className="p-6 border-b"><h3 className="text-lg font-semibold">Blocked Requests</h3></div>
          <div className="divide-y">
            {loading ? (
              <div className="p-6">Loading...</div>
            ) : blockedRequests.length === 0 ? (
              <div className="p-6 text-gray-500">No blocked requests</div>
            ) : (
              blockedRequests.map((b) => (
                <div key={b.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{b.ip_address} <span className="text-sm text-gray-500">{b.method} {b.path}</span></div>
                      <div className="text-sm text-gray-500">{b.reason}</div>
                      <div className="text-xs text-gray-400">{new Date(b.blocked_at).toLocaleString()}</div>
                    </div>
                    <button
                      onClick={() => handleExemptFromRow(b.ip_address)}
                      className="px-3 py-1 border rounded text-sm"
                    >
                      Exempt this IP
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AdminSecurity;
